import axios, { type AxiosResponse } from 'axios'
import BaseService from './BaseService'
import { require } from '@/utils/guards'

const ACCOMMODATIONS_API = `${import.meta.env.VITE_NEW_ACCOMMODATIONS_API_URL}/api/v1/performance/accommodation`
const GRAPHQL_API = `${import.meta.env.VITE_HOTEL_SEARCH_GRAPHQL_API_URL}`
const GRAPHQL_CLIENT_NAME = 'trivago-business-studio'

// ============================================================================
// GraphQL Queries
// ============================================================================

const getPartnerNames = `query ($input: AdvertiserDetailsInput!) {
  getAdvertiserDetails(input: $input) {
    advertiserDetails {
      nsid {
        id
        ns
      }
      translatedName {
        value
      }
      originalName {
        value
      }
    }
  }
}`

// ============================================================================
// Request/Response Types
// ============================================================================

export interface ConversionFunnelRequest {
  accommodationId: number
  startDate: string
  endDate: string
  breakout: string
}

/** Variation vs. the previous period; variationOverPeriod is only ever null by unenforced Kotlin default, never in practice */
export interface MetricVariation {
  previousValue: number
  variationOverPeriod: number
}

/** Partner ID -> value breakdown */
export type PartnerBreakdown = Record<string, number>

/** [timestamp, value], or [] when the backend has no value for that bucket (notably clickThroughRate) */
export type GraphDataPoint = [number, number] | []

/** value is null when there's no data in the requested date range; clickThroughRate's variation is never populated by the backend */
export interface ConversionMetric {
  value: number | null
  variation: MetricVariation | null
  partners: PartnerBreakdown
  graph: GraphDataPoint[]
}

export interface ConversionFunnelResponse {
  impressions: ConversionMetric
  clicks: ConversionMetric
  clickThroughRate: ConversionMetric
}

export interface PartnerNsid {
  id: number
  ns: number
}

export interface PartnerAdvertiserDetail {
  nsid: PartnerNsid
  translatedName: {
    value: string
  }
  originalName: {
    value: string
  }
}

export interface PartnerNamesResponse {
  data: {
    getAdvertiserDetails: {
      advertiserDetails: PartnerAdvertiserDetail[]
    }
  }
}

// ============================================================================
// DashboardService Class
// ============================================================================

class DashboardService extends BaseService {
  constructor() {
    super({
      baseUrl: ACCOMMODATIONS_API,
    })
  }

  fetchConversionFunnel(request: ConversionFunnelRequest): Promise<AxiosResponse<ConversionFunnelResponse>> {
    const { accommodationId, startDate, endDate, breakout } = request
    require(accommodationId)

    return axios({
      url: `${this.baseUrl}/${accommodationId}/conversion?startDate=${startDate}&endDate=${endDate}&dateAggregation=${breakout}`,
      method: 'GET',
      headers: this.studioHeadersWithAttribution(),
    })
  }

  fetchPartnersName(partnerIds: number[] | null | undefined): Promise<AxiosResponse<PartnerNamesResponse>> | undefined {
    if (!partnerIds || partnerIds.length === 0) return undefined

    const input: PartnerNsid[] = partnerIds.map(item => ({
      id: item,
      ns: 400,
    }))

    return axios({
      url: `${GRAPHQL_API}`,
      method: 'POST',
      data: {
        query: getPartnerNames,
        variables: {
          input: { nsids: input },
        },
      },
      headers: this.graphqlHeaders('en-US', GRAPHQL_CLIENT_NAME),
    })
  }
}

/** Singleton dashboard service instance */
export default new DashboardService()
